import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { videoSort } from '../util/constants';
import moment from 'moment';
import { Box, Text } from 'rebass/styled-components';
import useVideosListener from 'hooks/useVideosListener';
import { motion } from 'framer-motion';
import VideoContainer from './videoComponents/videoContainer';
import { VideoSortBar } from './videoComponents/videoSortBar';
import RowIconButton from './shell/RowIconButton';
import { ChevronIcon, DeleteIcon } from './shell/Icons';
import firebase from 'firebase/app';
import { VIDEO_DRAG_TYPE } from './videoComponents/video';

const Bar = styled(Box)`
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
`;

const Container = styled(Box)<{ dropped: boolean }>`
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  /* UI refresh (5 Nov 2026): an open record gets a light grey heading, not charcoal. */
  ${Bar} {
    background-color: ${(p) => (p.dropped ? '#F9FAFB' : '#FFFFFF')};
  }
`;

const recordInput = (size: number, weight: number, color: string): React.CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  height: 34,
  border: '1px solid transparent',
  borderRadius: 8,
  padding: '0 8px',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: size,
  fontWeight: weight,
  color,
  minWidth: 0,
});

const InputContainer = styled.div<{ width: string }>`
  width: ${(p) => p.width};
  min-width: ${(p) => (p.width === '100%' ? '150px' : p.width)};
  box-sizing: border-box;
`;

const DateText = styled(Text)<{ dropped: boolean }>`
  text-align: center;
`;

const Dropdown = styled(motion.div)`
  background-color: ${(p) => p.theme.colors.offWhite};
  display: flex;
  flex-direction: column;
`;

type Props = {
  record: IRecord;
  deleteRecord(): void;
  updateRecord(id: string, record: Partial<IRecord>): void;
};

const Record: React.FC<Props> = ({ record, deleteRecord, updateRecord }) => {
  const [currentSort, setCurrentSort] = useState<{
    sortType: videoSort;
    direction: 'desc' | 'asc';
  }>({ sortType: videoSort.date, direction: 'asc' });

  const [uploading, setUploading] = useState(false);
  const { videos, updateSort } = useVideosListener(record.id);
  const [dropped, setDropped] = useState(false);
  const [keyNumber, setKeyNumber] = useState(record.keyNumber);
  const [title, setTitle] = useState(record.title);
  const [dragOver, setDragOver] = useState(false);
  const [moveNote, setMoveNote] = useState('');

  // A file dragged from another record in this project lands here: only its record changes. The file
  // itself stays where it is in storage (Aaron, 5 Nov 2026).
  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types || []).includes(VIDEO_DRAG_TYPE);
  const onDragOver = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!dragOver) setDragOver(true);
  };
  const onDrop = async (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    setDragOver(false);
    let data: { id?: string; recordID?: string; projectID?: string } = {};
    try {
      data = JSON.parse(e.dataTransfer.getData(VIDEO_DRAG_TYPE));
    } catch {
      return;
    }
    if (!data.id || data.recordID === record.id) return;
    if (data.projectID && data.projectID !== record.projectID) {
      setMoveNote('Files can only be dragged between records of the same project.');
      return;
    }
    try {
      await firebase.firestore().collection('videos').doc(data.id).update({ recordID: record.id });
      setMoveNote(`Moved “${data.id}” here.`);
      setDropped(true);
    } catch (err) {
      setMoveNote(`Couldn't move it: ${(err as Error).message}`);
    }
    setTimeout(() => setMoveNote(''), 5000);
  };

  useEffect(() => {
    if (title && keyNumber) {
      if (record.title !== title) {
        setTitle(record.title);
      }
      if (record.keyNumber !== keyNumber) {
        setKeyNumber(record.keyNumber);
      }
    }
  }, [record]);

  useEffect(() => {
    updateSort(currentSort);
  }, [currentSort]);

  return (
    <>
      <Container
        dropped={dropped}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={(e: React.DragEvent) => {
          if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setDragOver(false);
        }}
        onDrop={onDrop}
        sx={{
          // A lighter rounded card inside the rounded Videos & Audio bucket (Aaron, 5 Nov 2026).
          border: dragOver ? '2px dashed #E5056A' : '1px solid #E4E6EA',
          borderRadius: '12px',
          overflowY: 'hidden',
          boxShadow: 'none',
          background: dragOver ? '#FFF5FA' : undefined,
        }}
      >
        <Bar className="archy-record-bar" px={[2, 3]} sx={{ height: 60, gap: '10px', borderBottom: dropped ? '1px solid #EEF0F2' : 'none' }}>
          <RowIconButton label={dropped ? 'Hide files' : 'Show files'} disabled={uploading} onClick={() => setDropped((current) => !current)}>
            <ChevronIcon up={dropped} color="#2E333B" />
          </RowIconButton>
          <InputContainer width={'150px'} className="archy-record-key">
            <input
              className="archy-edit"
              aria-label="Key or catalogue number"
              value={keyNumber || ''}
              placeholder="Key / cat no."
              onChange={(e) => setKeyNumber(e.target.value)}
              onBlur={(e) => updateRecord(record.id, { keyNumber: e.target.value })}
              style={recordInput(13, 500, '#5B616E')}
            />
          </InputContainer>
          <InputContainer width={'100%'} className="archy-record-title">
            <input
              className="archy-edit"
              aria-label="Record title"
              value={title || ''}
              placeholder="Record title"
              onChange={(e) => setTitle(e.target.value)}
              onBlur={(e) => updateRecord(record.id, { title: e.target.value })}
              style={recordInput(14, 600, '#16181D')}
            />
          </InputContainer>
          {(dragOver || moveNote) && (
            <span role="status" style={{ fontSize: 12, fontWeight: 600, color: '#E5056A', whiteSpace: 'nowrap', flex: 'none' }}>
              {dragOver ? 'Drop to move the file into this record' : moveNote}
            </span>
          )}
          <InputContainer width={'92px'} className="archy-record-date">
            <DateText dropped={dropped} sx={{ fontSize: '13px', color: '#5B616E', whiteSpace: 'nowrap' }}>
              {moment.unix(record.date).format('D MMM YYYY')}
            </DateText>
          </InputContainer>
          <RowIconButton label="Delete record" danger onClick={deleteRecord}>
            <DeleteIcon color="#B3261E" size={16} />
          </RowIconButton>
        </Bar>
        <Dropdown
          animate={dropped ? 'visible' : 'hidden'}
          transition={{ ease: 'easeInOut', duration: 0.25 }}
          variants={{ hidden: { height: 0 }, visible: { height: 400 } }}
        >
          <VideoContainer
            files={videos}
            record={record}
            isUploading={setUploading}
            header={<VideoSortBar currentSort={currentSort} update={(sortType, direction) => setCurrentSort({ sortType, direction })} />}
          />
        </Dropdown>
      </Container>
    </>
  );
};

export default Record;
